import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Typography, useTheme } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton/AcxButton";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import ResearchFindingsStore from "stores/ResearchFindingsStore";
import type { ResearchFindingsInitialState } from "stores/ResearchFindingsStore";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import EmailPreviewDialog from "./EmailPreviewDialog";
import QuestionFilterTabs from "./QuestionFilterTabs";
import ResearchQuestionCard from "./ResearchQuestionCard";
import RunDetailHeader from "./RunDetailHeader";
import RunDetailSkeleton from "./RunDetailSkeleton";
import RunListPanel from "./RunListPanel";

const PAGE_QUERY_PARAM = "page";
const RUN_ID_QUERY_PARAM = "runId";

function parsePageParam(searchParams: URLSearchParams): number {
    const parsed = Number.parseInt(
        searchParams.get(PAGE_QUERY_PARAM) ?? "1",
        10,
    );
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

function parseInitialState(
    searchParams: URLSearchParams,
): ResearchFindingsInitialState {
    return {
        page: parsePageParam(searchParams),
        runId: searchParams.get(RUN_ID_QUERY_PARAM) ?? undefined,
    };
}

const OrganizationResearchFindings: React.FC = observer(() => {
    const theme = useTheme();
    const store = useStore(ResearchFindingsStore);
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const orgId = authStore.activeOrgId;
    const [searchParams, setSearchParams] = useSearchParams();
    const initialStateRef = useRef<
        (ResearchFindingsInitialState & { orgId: string }) | undefined
    >();

    if (orgId && initialStateRef.current?.orgId !== orgId) {
        initialStateRef.current = {
            orgId,
            ...parseInitialState(searchParams),
        };
    }

    useEffect(() => {
        if (!orgId) return;
        const initialState = initialStateRef.current;
        store.loadRuns(orgId, initialState).catch((err: unknown) => {
            messageStore.logError(
                err instanceof Error
                    ? err.message
                    : "Failed to load Research Findings",
            );
        });
        return () => store.resetState();
    }, [store, messageStore, orgId]);

    const setResearchFindingSearchParams = useCallback(
        (page: number, runId?: string) => {
            const next = new URLSearchParams(searchParams);
            next.set(PAGE_QUERY_PARAM, String(page));
            if (runId) {
                next.set(RUN_ID_QUERY_PARAM, runId);
            } else {
                next.delete(RUN_ID_QUERY_PARAM);
            }
            if (next.toString() !== searchParams.toString()) {
                setSearchParams(next, { replace: true });
            }
        },
        [searchParams, setSearchParams],
    );

    const handleSelectRun = useCallback(
        (runId: string) => {
            store.selectRun(runId);
            setResearchFindingSearchParams(store.runsPage, runId);
        },
        [store, setResearchFindingSearchParams],
    );

    const handlePageChange = useCallback(
        (page: number) => {
            store.clearSelectedRun();
            setResearchFindingSearchParams(page);
            void store.setRunsPage(page);
        },
        [store, setResearchFindingSearchParams],
    );

    const handleRefresh = useCallback(() => {
        void store.refreshCurrentPage();
    }, [store]);

    const runStore = store.selectedRunStore;

    const renderDetail = () => {
        if (store.isLoading) {
            return <RunDetailSkeleton />;
        }
        if (store.runs.length === 0) {
            return (
                <Typography sx={{ color: theme.palette.gray[500] }}>
                    No research runs yet for this organization.
                </Typography>
            );
        }
        if (!runStore) {
            return (
                <Typography sx={{ color: theme.palette.gray[500] }}>
                    Select a research run to view its findings.
                </Typography>
            );
        }
        if (runStore.isLoading) {
            return <RunDetailSkeleton />;
        }
        return (
            <>
                <RunDetailHeader store={runStore} />
                <QuestionFilterTabs store={runStore} />
                {runStore.filteredQuestions.length === 0 ? (
                    <Typography sx={{ color: theme.palette.gray[500], mt: 1 }}>
                        No questions match this filter.
                    </Typography>
                ) : (
                    runStore.filteredQuestions.map((q) => (
                        <ResearchQuestionCard
                            key={q.id}
                            question={q}
                            store={runStore}
                        />
                    ))
                )}
                <EmailPreviewDialog store={runStore} />
            </>
        );
    };

    return (
        <Box
            sx={{
                padding: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1200px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <OrganizationBreadcrumbs
                pageTitle="Research Findings"
                sectionLabel="KBo"
            />

            <Box sx={{ mt: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "24px",
                            fontWeight: 600,
                            color: theme.palette.gray[700],
                        }}
                    >
                        Research Findings
                    </Typography>
                    <AcxButton
                        color="white"
                        fullWidth={false}
                        startIcon={<RefreshRoundedIcon />}
                        disabled={store.isLoading}
                        onClick={handleRefresh}
                    >
                        Refresh
                    </AcxButton>
                </Box>
                <Typography
                    sx={{
                        fontSize: "14px",
                        color: theme.palette.gray[500],
                        mt: 0.5,
                    }}
                >
                    Review the questions KBo generated and the answers it
                    researched, run by run. Send the ones worth sharing with the
                    client, and give feedback so KBo improves.
                </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                <RunListPanel
                    store={store}
                    onPageChange={handlePageChange}
                    onSelectRun={handleSelectRun}
                />
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {renderDetail()}
                </Box>
            </Box>
        </Box>
    );
});

export default OrganizationResearchFindings;
